import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { ArrowLeft, Loader2, CheckCircle2 } from 'lucide-react';
import { useBusinessNav } from '@/hooks/useBusinessNav';
import { authApi } from '@/lib/api';
import { EmailVerificationModal } from '@/components/Modals/EmailVerificationModal';
import { useApp } from '@/components/AppLayout';

export function ProfileSettings() {
  const { nav } = useBusinessNav();
  const { refreshUser } = useApp();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [originalEmail, setOriginalEmail] = useState('');
  const [isEmailVerified, setIsEmailVerified] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [verificationModalOpen, setVerificationModalOpen] = useState(false);

  useEffect(() => {
    loadUser();
  }, []);

  const loadUser = async () => {
    const response = await authApi.getMe();
    if (response.data) {
      setFirstName(response.data.first_name || '');
      setLastName(response.data.last_name || '');
      setEmail(response.data.email || '');
      setOriginalEmail(response.data.email || '');
      setIsEmailVerified(response.data.is_email_verified || false);
    }
    setLoading(false);
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess(false);

    // Build update payload
    const payload: { first_name: string; last_name: string; email?: string } = {
      first_name: firstName,
      last_name: lastName,
    };

    // Only include email if unverified and changed
    if (!isEmailVerified && email !== originalEmail) {
      payload.email = email;
    }

    const response = await authApi.updateMe(payload);

    setSaving(false);

    if (response.error) {
      setError(response.error);
      return;
    }

    // Update original email if it was changed
    if (payload.email) {
      setOriginalEmail(email);
    }

    setSuccess(true);
    setTimeout(() => setSuccess(false), 3000);
  };

  const handleVerificationSuccess = async () => {
    setIsEmailVerified(true);
    refreshUser();
  };

  return (
    <div className="px-6 pt-6 pb-6">
      <div className="space-y-1 mb-8">
        <Button
          variant="ghost"
          size="sm"
          className="mb-2 -ml-2 text-muted-foreground"
          onClick={() => nav('/settings')}
        >
          <ArrowLeft className="mr-1 h-4 w-4" />
          Back to Settings
        </Button>
        <h1 className="text-2xl font-semibold tracking-tight">Personal Info</h1>
        <p className="text-sm text-muted-foreground">
          Update your personal information.
        </p>
      </div>

      <Card>
        <CardContent className="pt-6">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <div className="relative">
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={isEmailVerified}
                    className={isEmailVerified ? 'bg-muted pr-10' : ''}
                  />
                  {isEmailVerified && (
                    <CheckCircle2 className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-green-500" />
                  )}
                </div>
                {isEmailVerified ? (
                  <p className="text-xs text-muted-foreground flex items-center gap-1">
                    <span className="text-green-600">Email verified.</span> Cannot be changed.
                  </p>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    Email not verified.{' '}
                    <button
                      type="button"
                      className="text-blue-600 hover:underline"
                      onClick={() => setVerificationModalOpen(true)}
                    >
                      Verify now
                    </button>
                  </p>
                )}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="first-name">First Name</Label>
                  <Input
                    id="first-name"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder="John"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="last-name">Last Name</Label>
                  <Input
                    id="last-name"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    placeholder="Doe"
                  />
                </div>
              </div>
              {error && (
                <p className="text-sm text-destructive">{error}</p>
              )}
              {success && (
                <p className="text-sm text-green-600">Profile updated successfully!</p>
              )}
              <div className="flex justify-end">
                <Button onClick={handleSave} disabled={saving}>
                  {saving ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    'Save'
                  )}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <EmailVerificationModal
        open={verificationModalOpen}
        onOpenChange={setVerificationModalOpen}
        email={email}
        onSuccess={handleVerificationSuccess}
      />
    </div>
  );
}
